import { Alert, AlertIcon, Box, Button, Flex, FormControl, FormLabel, Heading, Spinner, Switch, Text } from "@chakra-ui/react";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGetEspdCriteriaQuery, useGetEspdRequestQuery, useUpdateEspdRequestMutation } from "../../../../../autogen/bff-api";
import { EspdCriterionSelector } from "./EspdCriterionSelector";

interface Props {
  previousStep: () => void;
  nextStep: () => void;
  eventId: string;
}

export const Espd = ({ previousStep, nextStep, eventId }: Props) => {
  const { t } = useTranslation();
  const { data: taxonomyData, isLoading: isLoadingTaxonomy } = useGetEspdCriteriaQuery({});
  const { data: requestData, isLoading: isLoadingRequest } = useGetEspdRequestQuery({ eventId });
  const [updateRequest, { isLoading: isSaving }] = useUpdateEspdRequestMutation();

  const [enabled, setEnabled] = useState(false);
  const [selectedCriterionIds, setSelectedCriterionIds] = useState<string[]>([]);

  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const selectedCriterionIdsRef = useRef(selectedCriterionIds);
  selectedCriterionIdsRef.current = selectedCriterionIds;

  useEffect(() => {
    if (requestData) {
      setEnabled(requestData.enabled);
      setSelectedCriterionIds(requestData.selectedCriterionIds ?? []);
    }
  }, [requestData]);

  const requiredCriterionIds = useMemo(() => {
    if (!taxonomyData?.categories) return [];
    return taxonomyData.categories
      .flatMap((cat) => cat.criteria)
      .filter((c) => c.required)
      .map((c) => c.id);
  }, [taxonomyData]);

  const getEffectiveSelectedIds = useCallback(() => {
    const ids = new Set(selectedCriterionIdsRef.current);
    requiredCriterionIds.forEach((id) => ids.add(id));
    return Array.from(ids);
  }, [requiredCriterionIds]);

  const effectiveSelectedIds = useMemo(() => {
    const ids = new Set(selectedCriterionIds);
    requiredCriterionIds.forEach((id) => ids.add(id));
    return Array.from(ids);
  }, [selectedCriterionIds, requiredCriterionIds]);

  const triggerSave = useCallback(async () => {
    await updateRequest({
      eventId,
      updateEspdRequestBody: {
        enabled: enabledRef.current,
        selectedCriterionIds: getEffectiveSelectedIds(),
      },
    });
  }, [eventId, getEffectiveSelectedIds, updateRequest]);

  const debouncedSave = useMemo(
    () => debounce(() => triggerSave(), 1000),
    [triggerSave]
  );
  useEffect(() => () => debouncedSave.cancel(), [debouncedSave]);

  const handleToggleEnabled = useCallback((newEnabled: boolean) => {
    setEnabled(newEnabled);
    debouncedSave();
  }, [debouncedSave]);

  const handleToggleCriterion = useCallback((id: string, selected: boolean) => {
    setSelectedCriterionIds((prev) => {
      const next = selected ? [...prev, id] : prev.filter((cid) => cid !== id);
      return next;
    });
    debouncedSave();
  }, [debouncedSave]);

  const handleNext = useCallback(async () => {
    debouncedSave.cancel();
    await triggerSave();
    nextStep();
  }, [debouncedSave, triggerSave, nextStep]);

  if (isLoadingTaxonomy || isLoadingRequest) {
    return (
      <Flex justifyContent="center" pt="10">
        <Spinner color="teal" />
      </Flex>
    );
  }

  return (
    <Box pt="4" pb="8">
      <Heading as="h3" size="md" mb="4">
        {t("ESPD — European Single Procurement Document")}
      </Heading>
      <Text fontSize="sm" color="smMuted" mb="6">
        {t("espd_buyer_intro")}
      </Text>

      <FormControl display="flex" alignItems="center" mb="6">
        <Switch
          id="espd-enabled"
          isChecked={enabled}
          onChange={(e) => handleToggleEnabled(e.target.checked)}
          mr="3"
        />
        <FormLabel htmlFor="espd-enabled" mb="0" fontWeight="medium">
          {t("Enable ESPD for this procurement")}
        </FormLabel>
      </FormControl>

      {enabled && taxonomyData?.categories && (
        <>
          <Alert status="info" mb="4" borderRadius="md">
            <AlertIcon />
            <Text fontSize="sm">
              {t("Required criteria are pre-selected and cannot be removed. Select additional criteria as needed.")}
            </Text>
          </Alert>

          <EspdCriterionSelector
            categories={taxonomyData.categories}
            selectedCriterionIds={effectiveSelectedIds}
            onToggleCriterion={handleToggleCriterion}
          />
        </>
      )}

      <Flex justifyContent="space-between" mt="6">
        <Button onClick={previousStep} variant="outline" colorScheme="teal" size="sm">
          {t("Previous")}
        </Button>
        <Flex gap="2">
          <Button onClick={handleNext} isLoading={isSaving} colorScheme="teal" size="sm">
            {t("Next")}
          </Button>
        </Flex>
      </Flex>
    </Box>
  );
};
